import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'

// ---------- Types (mirror api-server/src/services/support.service.ts) ----------

export interface SupportTicket {
  id: string
  reference: string
  subject: string
  description: string
  status: 'open' | 'pending' | 'resolved' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  assigned_to?: string
  assigned_to_name?: string
  source: string
  resolution?: string
  external_user_id?: string
  customer_email?: string
  customer_name?: string
  customer_external_id?: string
  resolved_at?: string
  closed_at?: string
  created_at: string
  updated_at: string
}

export interface SupportTicketReply {
  id: string
  ticket_id: string
  author_type: 'bot' | 'agent' | 'customer'
  agent_name?: string
  customer_name?: string
  customer_email?: string
  content: string
  is_internal: boolean
  created_at: string
}

export interface ExternalUser {
  id: string
  external_id?: string
  email?: string
  name?: string
  phone?: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface SupportCategory {
  id: string
  name: string
  description?: string
  order_index: number
  is_active: boolean
}

export interface SupportArticle {
  id: string
  category_id?: string
  category_name?: string
  title: string
  content: string
  summary?: string
  tags: string[]
  order_index: number
  is_active: boolean
  view_count: number
  created_at: string
  updated_at: string
}

export interface SupportConfig {
  mode: 'sales' | 'support' | 'both'
  enabled: boolean
  botName: string
  greeting: string
  placeholder: string
  showTicketButton: boolean
  showKbSuggestions: boolean
  humanHours: {
    start: string
    end: string
    timezone: string
    days: number[]
  }
  ticketForm: {
    askEmail: boolean
    askName: boolean
    askPhone: boolean
    categories: string[]
  }
  customerAreaVars?: {
    platform: 'php' | 'liquid' | 'wordpress' | 'javascript' | 'custom'
    userId: string
    name: string
    email: string
  }
}

export interface SupportAnalytics {
  tickets: { today: number; week: number; month: number; total: number }
  status: Record<string, number>
  resolution: Record<string, number>
  avgResolutionHours: number
  csat: { average: number; totalVotes: number }
  topArticles: { id: string; title: string; view_count: number }[]
}

// ---------- Helpers ----------

// The API returns 403 with this code on every /support/* route when the
// add-on is not active for the tenant.
export const isAddonRequiredError = (error: unknown): boolean =>
  (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'SUPPORT_ADDON_REQUIRED'

export const getTicketStatusColor = (status: string) => {
  switch (status) {
    case 'open': return 'bg-green-500/20 text-green-400'
    case 'pending': return 'bg-yellow-500/20 text-yellow-400'
    case 'resolved': return 'bg-blue-500/20 text-blue-400'
    case 'closed': return 'bg-gray-500/20 text-gray-400'
    default: return 'bg-gray-500/20 text-gray-400'
  }
}

export const getTicketPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'bg-red-500/20 text-red-400'
    case 'high': return 'bg-orange-500/20 text-orange-400'
    case 'medium': return 'bg-yellow-500/20 text-yellow-400'
    case 'low': return 'bg-green-500/20 text-green-400'
    default: return 'bg-gray-500/20 text-gray-400'
  }
}

// Toggle switch (same style as the notifications toggle in the sidebar)
export const Toggle: React.FC<{ checked: boolean; onChange: (value: boolean) => void; label?: string }> = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-[#006cd4]' : 'bg-gray-600 hover:bg-gray-500'
    }`}
    title={label}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
)

// Empty state shown when the add-on is not active (direct URL access)
export const AddonInactive: React.FC = () => {
  const { t } = useTranslation()
  return (
    <div className="bg-[#12121a] border border-white/10 rounded-xl p-12 text-center max-w-xl mx-auto mt-12">
      <div className="w-14 h-14 rounded-full bg-[#006cd4]/20 flex items-center justify-center text-[#006cd4] mx-auto mb-4">
        <Icon name="puzzle-piece" className="text-2xl" />
      </div>
      <h2 className="text-xl font-bold mb-2">{t('supportModule.inactiveTitle')}</h2>
      <p className="text-gray-400 text-sm mb-6">{t('supportModule.inactiveDesc')}</p>
      <Link
        to="/billing"
        className="inline-block px-6 py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors"
      >
        {t('supportModule.activateCta')}
      </Link>
    </div>
  )
}
